import { lazy, type ComponentType } from 'react';

// ============================================================================
// lazyWithReload (aus routes.tsx herausgeloest, R2-07) — Stale-Deploy-/Chunk-
// Heilung fuer alle Lazy-Routen.
// ----------------------------------------------------------------------------
// Jede Route ist ein eigener Lazy-Chunk. Schlaegt der dynamische Import fehl
// (alte/ge-cachte index.html referenziert einen nicht mehr vorhandenen
// Chunk-Hash, ein Netz-Glitch, oder ein inkonsistentes Bundle), wirft React und
// Suspense reicht den Fehler an die RouteErrorBoundary durch → der User sieht
// den "Verbindungsfehler"-Screen und kommt NICHT mehr raus, weil die abgelehnte
// Import-Promise gecacht bleibt (Retry/Navigation heilen nicht). Abhilfe: EINMAL
// pro Session hart neu laden — die frische index.html bringt korrekte Hashes fuer
// ALLE Routen. Der sessionStorage-Guard verhindert eine Reload-Schleife (zweiter
// Fehlschlag → echte Boundary). Greift fuer native (gebuendeltes dist) wie remote
// (Wrapper).
//
// Eigenes Modul, damit routes.tsx UND die Teil-Routentabellen (characterRoutes)
// dieselbe Heilung nutzen, ohne sich gegenseitig zu importieren.
// ============================================================================
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      // Echte Ursache des fehlgeschlagenen Chunk-Imports in die nativen Logs
      // spiegeln (Safari Web Inspector / adb logcat), damit ein Folgefehler
      // nicht hinter dem generischen "Verbindungsfehler" verschwindet.
      // eslint-disable-next-line no-console
      console.error('[LorathChunkLoad] dynamic import failed', error);
      const KEY = 'lorath:chunk-reload';
      if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, '1');
        window.location.reload();
        // Reload läuft — nie auflösen, damit die Boundary nicht kurz aufblitzt.
        return new Promise<{ default: T }>(() => {
          // Bewusst leer: der Reload uebernimmt, diese Promise darf nie aufloesen.
        });
      }
      throw error;
    }),
  );
}
